<template>
  <div class="a11y-menu" ref="rootRef" @keydown.esc="closeMenu">
    <button
      type="button"
      class="a11y-trigger"
      :class="{ open: isOpen, active: hasActiveSettings }"
      @click="isOpen = !isOpen"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      :aria-label="t('a11y.menu.open')"
      :title="t('a11y.menu.open')"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="6" cy="15" r="4"/>
        <circle cx="18" cy="15" r="4"/>
        <path d="M14 15a2 2 0 0 0-2-2 2 2 0 0 0-2 2"/>
        <path d="M2.5 13 5 7c.7-1.3 1.4-2 3-2"/>
        <path d="M21.5 13 19 7c-.7-1.3-1.5-2-3-2"/>
      </svg>
    </button>

    <Teleport to="body">
      <transition name="a11y-drop">
        <div
          v-if="isOpen"
          ref="panelRef"
          class="a11y-panel"
          :style="panelStyle"
          role="group"
          :aria-label="t('a11y.menu.title')"
        >
          <div class="a11y-panel-title">{{ t('a11y.menu.title') }}</div>

          <div class="a11y-setting">
            <span class="a11y-setting-label" id="a11y-font-label">{{ t('a11y.menu.fontSize') }}</span>
            <div class="a11y-font-buttons" role="group" aria-labelledby="a11y-font-label">
              <button
                v-for="option in fontOptions"
                :key="option.scale"
                type="button"
                class="a11y-font-btn"
                :class="{ active: a11yStore.fontScale === option.scale }"
                :style="{ fontSize: option.size }"
                :aria-pressed="a11yStore.fontScale === option.scale"
                :aria-label="option.label"
                @click="a11yStore.setFontScale(option.scale)"
              >А</button>
            </div>
          </div>

          <button
            type="button"
            class="a11y-toggle"
            :aria-pressed="isLightTheme"
            @click="themeStore.setTheme(isLightTheme ? 'dark' : 'light')"
          >
            <span>{{ t('a11y.menu.lightTheme') }}</span>
            <span class="a11y-switch" :class="{ on: isLightTheme }" aria-hidden="true"></span>
          </button>

          <button
            type="button"
            class="a11y-toggle"
            :aria-pressed="a11yStore.highContrast"
            @click="a11yStore.toggleHighContrast()"
          >
            <span>{{ t('a11y.menu.highContrast') }}</span>
            <span class="a11y-switch" :class="{ on: a11yStore.highContrast }" aria-hidden="true"></span>
          </button>

          <button
            type="button"
            class="a11y-toggle"
            :aria-pressed="a11yStore.reduceMotion"
            @click="a11yStore.toggleReduceMotion()"
          >
            <span>{{ t('a11y.menu.reduceMotion') }}</span>
            <span class="a11y-switch" :class="{ on: a11yStore.reduceMotion }" aria-hidden="true"></span>
          </button>

          <div class="a11y-panel-footer">
            <button
              type="button"
              class="a11y-reset"
              :disabled="!hasActiveSettings"
              @click="resetAll"
            >{{ t('a11y.menu.reset') }}</button>
            <router-link to="/help#accessibility" class="a11y-more" @click="closeMenu">
              {{ t('a11y.menu.moreInfo') }}
            </router-link>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useAccessibilityStore, type FontScale } from '@/stores/accessibility'
import { useThemeStore } from '@/stores/theme'
import { useI18n } from '@/composables/useI18n'

const props = withDefaults(defineProps<{
  dropUp?: boolean
}>(), {
  dropUp: false
})

const { t } = useI18n()
const a11yStore = useAccessibilityStore()
const themeStore = useThemeStore()

const isLightTheme = computed(() => themeStore.currentTheme === 'light')

const isOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)

const PANEL_WIDTH_REM = 17.5

// Панель телепортируется в body и позиционируется fixed относительно кнопки,
// чтобы её не обрезали overflow / backdrop-filter на лендинге и /login.
// getBoundingClientRect — уже после zoom, а left/top задаются до него.
const panelStyle = ref<Record<string, string>>({})

function currentZoom() {
  const raw = getComputedStyle(document.body).zoom
  if (!raw || raw === 'normal') return 1
  if (raw.endsWith('%')) {
    const percent = parseFloat(raw)
    return Number.isFinite(percent) && percent > 0 ? percent / 100 : 1
  }
  const value = parseFloat(raw)
  return Number.isFinite(value) && value > 0 ? value : 1
}

function getPanelWidth(zoom: number) {
  const desired = PANEL_WIDTH_REM * 16
  const max = (window.innerWidth - 16) / zoom
  return Math.max(160, Math.min(desired, max))
}

function updatePanelPosition() {
  if (!rootRef.value) return
  const zoom = currentZoom()
  const rect = rootRef.value.getBoundingClientRect()
  const top = rect.top / zoom
  const bottom = rect.bottom / zoom
  const left = rect.left / zoom
  const right = rect.right / zoom
  const panelWidth = getPanelWidth(zoom)
  const viewW = window.innerWidth / zoom
  const viewH = window.innerHeight / zoom
  const gutter = 8 / zoom
  const maxLeft = viewW - panelWidth - gutter
  const style: Record<string, string> = {
    position: 'fixed',
    width: `${panelWidth}px`,
    overflowY: 'auto',
    zIndex: '400'
  }
  if (props.dropUp) {
    style.bottom = `${viewH - top + 6 / zoom}px`
    style.left = `${Math.max(gutter, Math.min(left, maxLeft))}px`
    style.maxHeight = `${Math.max(120 / zoom, top - 16 / zoom)}px`
  } else {
    style.top = `${bottom + 6 / zoom}px`
    style.left = `${Math.max(gutter, Math.min(right - panelWidth, maxLeft))}px`
    style.maxHeight = `${Math.max(120 / zoom, viewH - bottom - 16 / zoom)}px`
  }
  panelStyle.value = style
}

watch(isOpen, (open) => {
  if (open) updatePanelPosition()
})

watch(() => a11yStore.fontScale, () => {
  if (isOpen.value) updatePanelPosition()
})

function handleViewportChange() {
  if (isOpen.value) updatePanelPosition()
}

const fontOptions = computed<{ scale: FontScale; size: string; label: string }[]>(() => [
  { scale: 100, size: '0.875rem', label: t('a11y.menu.fontNormal') },
  { scale: 125, size: '1.0625rem', label: t('a11y.menu.fontLarge') },
  { scale: 150, size: '1.25rem', label: t('a11y.menu.fontExtraLarge') }
])

const hasActiveSettings = computed(() =>
  a11yStore.fontScale !== 100 || a11yStore.highContrast || a11yStore.reduceMotion || isLightTheme.value
)

function resetAll() {
  a11yStore.reset()
  themeStore.setTheme('dark')
}

function closeMenu() {
  isOpen.value = false
}

function handleClickOutside(event: Event) {
  const target = event.target as Node
  if (rootRef.value?.contains(target) || panelRef.value?.contains(target)) return
  isOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', handleViewportChange, true)
  window.addEventListener('resize', handleViewportChange)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleViewportChange, true)
  window.removeEventListener('resize', handleViewportChange)
})
</script>

<style scoped>
.a11y-menu {
  position: relative;
}

.a11y-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: 1px solid var(--text-primary);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-normal);
}

.a11y-trigger:hover {
  background: transparent;
  color: var(--text-primary);
  border-color: var(--text-primary);
}

.a11y-trigger.open,
.a11y-trigger.active {
  color: var(--primary-blue);
  border-color: var(--primary-blue);
}

@media (max-width: 768px) {
  .a11y-trigger {
    width: 36px;
    height: 36px;
  }
}
</style>

<style>
/* Teleported panel lives outside the component tree — must not be scoped */
.a11y-panel {
  background: var(--background-secondary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-lg, 12px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  padding: 0.875rem;
}

[data-theme="light"] .a11y-panel {
  background: #fff;
  border-color: #e5e7eb;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.a11y-panel-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
}

.a11y-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.a11y-setting-label {
  font-size: 0.875rem;
  color: var(--text-secondary);
  min-width: 0;
  flex: 1 1 auto;
  overflow-wrap: break-word;
}

.a11y-font-buttons {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.a11y-font-btn {
  display: flex;
  align-items: end;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding-bottom: 6px;
  background: var(--background-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.a11y-font-btn:hover {
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.a11y-font-btn.active {
  border-color: var(--primary-blue);
  color: var(--primary-blue);
  background: rgba(var(--primary-green-rgb), 0.08);
}

.a11y-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.a11y-toggle > span:first-child {
  min-width: 0;
  overflow-wrap: break-word;
}

.a11y-toggle:hover {
  color: var(--text-primary);
}

.a11y-switch {
  flex-shrink: 0;
  width: 36px;
  height: 20px;
  background: var(--background-elevated);
  border: 1px solid var(--border-secondary);
  border-radius: var(--radius-full);
  position: relative;
  transition: background var(--transition-fast);
}

.a11y-switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--text-secondary);
  transition: transform var(--transition-fast), background var(--transition-fast);
}

.a11y-switch.on {
  background: var(--primary-blue);
  border-color: var(--primary-blue);
}

.a11y-switch.on::after {
  transform: translateX(16px);
  background: #fff;
}

.a11y-panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.625rem;
  padding-top: 0.625rem;
  border-top: 1px solid var(--border-primary);
  flex-wrap: wrap;
}

.a11y-reset {
  background: none;
  border: none;
  color: var(--text-tertiary);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

.a11y-reset:hover:not(:disabled) {
  color: var(--text-primary);
}

.a11y-reset:disabled {
  opacity: 0.5;
  cursor: default;
  text-decoration: none;
}

.a11y-more {
  font-size: 0.8125rem;
  color: var(--primary-blue);
  font-weight: 600;
  text-decoration: none;
}

.a11y-more:hover {
  text-decoration: underline;
}

.a11y-drop-enter-active {
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}
.a11y-drop-leave-active {
  transition: all 0.12s ease-in;
}
.a11y-drop-enter-from {
  opacity: 0;
  transform: translateY(-6px) scale(0.97);
}
.a11y-drop-leave-to {
  opacity: 0;
  transform: translateY(-3px) scale(0.98);
}

/* «Отключить анимации»: панель появляется без сдвига/масштаба, переключатели без плавности */
[data-a11y-reduce-motion="true"] .a11y-drop-enter-active,
[data-a11y-reduce-motion="true"] .a11y-drop-leave-active,
[data-a11y-reduce-motion="true"] .a11y-switch,
[data-a11y-reduce-motion="true"] .a11y-switch::after,
[data-a11y-reduce-motion="true"] .a11y-font-btn {
  transition: none;
}

[data-a11y-reduce-motion="true"] .a11y-drop-enter-from,
[data-a11y-reduce-motion="true"] .a11y-drop-leave-to {
  transform: none;
}
</style>
